
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotFound from '@/pages/NotFound';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  is404Error: boolean;
}

// This component wraps the error boundary with hooks access
export const NavigationErrorBoundary: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  return (
    <NavigationErrorBoundaryClass 
      location={location} 
      navigate={navigate}
    >
      {children}
    </NavigationErrorBoundaryClass>
  );
};

// The actual error boundary class component
class NavigationErrorBoundaryClass extends Component<
  Props & { location: ReturnType<typeof useLocation>; navigate: ReturnType<typeof useNavigate> },
  State
> {
  public state: State = {
    hasError: false,
    is404Error: false
  };

  // When the location changes, reset the error state
  public static getDerivedStateFromProps(
    props: Props & { location: ReturnType<typeof useLocation> },
    state: State
  ): State | null {
    // Reset error state on location change
    if (state.hasError) {
      return { hasError: false, is404Error: false };
    }
    return null;
  }

  public static getDerivedStateFromError(error: Error): State {
    // Check if this is a 404-type error
    const is404 = error.message.includes('not found') || 
                  error.message.includes('404') ||
                  error.message.includes('route') ||
                  error.message.toLowerCase().includes('page');
    
    return { 
      hasError: true, 
      is404Error: is404
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Navigation error:", error, errorInfo);
    
    // Log to analytics or monitoring service if available
    // analyticsService.logError('navigation_error', { path: this.props.location.pathname, error: error.message });
  }

  public render() {
    if (this.state.hasError) {
      if (this.state.is404Error) {
        return <NotFound />;
      }
      
      // For other errors, maybe we want a different component
      return <NotFound />;
    }

    return this.props.children;
  }
}

export default NavigationErrorBoundary;
